// ─── CSV Download ─────────────────────────────────────────────────────────────

export interface CsvColumn<T = Record<string, unknown>> {
  label: string
  getValue: (row: T) => string | number | boolean | null | undefined
}

export function generateCsv<T = Record<string, unknown>>(
  rows: T[],
  columns: CsvColumn<T>[],
): string {
  const header = columns.map((c) => quoteCell(c.label)).join(',')
  const body = rows
    .map((row) =>
      columns.map((c) => quoteCell(String(c.getValue(row) ?? ''))).join(','),
    )
    .join('\r\n')
  return `${header}\r\n${body}${body ? '\r\n' : ''}`
}

export function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function quoteCell(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

// ─── CSV Upload / Parse ───────────────────────────────────────────────────────

export interface ParsedCsvRow {
  [key: string]: string
}

export interface ParsedCsv {
  headers: string[]
  rows: ParsedCsvRow[]
}

export function parseCsv(text: string): ParsedCsv {
  // Normalize line endings
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = splitCsvLines(normalized)

  if (lines.length === 0) return { headers: [], rows: [] }

  const headers = parseCsvLine(lines[0]!)
  const rows: ParsedCsvRow[] = []

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]!.trim()
    if (!line) continue
    const values = parseCsvLine(line)
    const row: ParsedCsvRow = {}
    headers.forEach((header, idx) => {
      row[header] = values[idx] ?? ''
    })
    rows.push(row)
  }

  return { headers, rows }
}

function splitCsvLines(text: string): string[] {
  // Split by newlines that are not inside quoted fields
  const lines: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"'
        i++ // skip escaped quote
      } else {
        inQuotes = !inQuotes
        current += ch
      }
    } else if (ch === '\n' && !inQuotes) {
      lines.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  if (current) lines.push(current)
  return lines
}

function parseCsvLine(line: string): string[] {
  const values: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (ch === ',' && !inQuotes) {
      values.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  values.push(current.trim())
  return values
}

// ─── Email List Parser ────────────────────────────────────────────────────────

export interface ParsedEmailEntry {
  email: string
  firstName: string
  lastName: string
  language?: string       // ISO 639-1 code, e.g. 'en', 'fr'
  segments?: string[]     // MailingListSegment values
  isValid: boolean
}

export function parseEmailList(rawText: string): ParsedEmailEntry[] {
  return rawText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line): ParsedEmailEntry => {
      // "First Last <email@example.com>" format
      const angleMatch = line.match(/^(.+?)\s*<([^>]+)>\s*$/)
      if (angleMatch) {
        const nameParts = angleMatch[1]!.trim().split(/\s+/)
        const email = angleMatch[2]!.trim().toLowerCase()
        return {
          email,
          firstName: nameParts[0] ?? '',
          lastName: nameParts.slice(1).join(' '),
          isValid: isValidEmail(email),
        }
      }
      // Plain email
      const email = line.toLowerCase()
      return { email, firstName: '', lastName: '', isValid: isValidEmail(email) }
    })
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}
